/**
 * Loading state for the schema index and for one schema.
 *
 * Small on purpose. The interesting part is the ERROR branch: a schema that will not load
 * must produce a sentence an admin can act on, because the two ways it fails - the contract
 * mirror is not being served in dev, the artifact route is not reachable in prod - have
 * completely different fixes and are indistinguishable from a spinner.
 */
import { useEffect, useMemo, useState } from 'react';

import type { SchemaNode } from '../blocks/types';
import { resolveSchemaBase, SchemaSource, type SchemaIndex } from './source';

export type Loadable<T> = { readonly state: 'loading' } | { readonly state: 'ready'; readonly value: T } | { readonly state: 'error'; readonly message: string };

/** The source built from the build-time environment. Memoised so it is one object per app. */
export function useSchemaSource(): SchemaSource {
  return useMemo(() => new SchemaSource({ base: resolveSchemaBase(import.meta.env as unknown as Record<string, string | boolean | undefined>) }), []);
}

function useLoadable<T>(load: (signal: AbortSignal) => Promise<T>, deps: readonly unknown[]): Loadable<T> {
  const [result, setResult] = useState<Loadable<T>>({ state: 'loading' });

  useEffect(() => {
    const controller = new AbortController();
    setResult({ state: 'loading' });
    load(controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setResult({ state: 'ready', value });
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setResult({ state: 'error', message: cause instanceof Error ? cause.message : String(cause) });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return result;
}

export function useSchemaIndex(source: SchemaSource): Loadable<SchemaIndex> {
  return useLoadable(() => source.index(), [source]);
}

export function useSchema(source: SchemaSource, id: string | undefined): Loadable<SchemaNode> {
  return useLoadable(async () => {
    if (id === undefined || id === '') throw new Error('No schema was named in the URL.');
    return source.schema(id);
  }, [source, id]);
}
